import { Link } from "@tanstack/react-router";
import { useT, useMarket } from "@/i18n";
import { CountrySwitcher } from "./CountrySwitcher";
import { ShieldCheck, Leaf, Sparkles } from "lucide-react";

type FooterLink = { to: string; label: string };

function FooterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      {/* Mobile: collapsible accordion */}
      <details className="group border-b border-border md:hidden">
        <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">
          <span>{title}</span>
          <span aria-hidden className="text-base text-muted-foreground transition-transform group-open:rotate-45">+</span>
        </summary>
        <div className="pb-4">{children}</div>
      </details>
      {/* Desktop: static column */}
      <div className="hidden md:block">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">{title}</h3>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

function LinkList({ links }: { links: FooterLink[] }) {
  return (
    <ul className="space-y-3 text-sm text-foreground/80">
      {links.map((l) => (
        <li key={l.to + l.label}>
          <Link to={l.to} className="transition-colors hover:text-clay">{l.label}</Link>
        </li>
      ))}
    </ul>
  );
}

export function SiteFooter() {
  const t = useT();
  const market = useMarket();
  const isUs = market === "us";
  const shopLinks: FooterLink[] = [
    { to: "/pikler-kolmio", label: t("footer.link.pikler") },
    { to: "/oppimistorni", label: t("footer.link.tower") },
    { to: "/montessori-sanky", label: t("footer.link.bed") },
    { to: "/vaaterekki", label: t("footer.link.clothes") },
    { to: "/montessori-lelut", label: t("footer.link.toys") },
    { to: "/iglu", label: t("shop.iglu") },
    { to: "/kauppa", label: t("footer.shopAll") },
  ];
  const supportLinks: FooterLink[] = [
    { to: "/meista", label: t("nav.about") },
    { to: "/yhteystiedot", label: t("footer.contact") },
    { to: "/toimitus", label: t("footer.shipping") },
    { to: "/palautus", label: t("footer.returns") },
    { to: "/ukk", label: t("nav.faq") },
  ];
  const legalLinks: FooterLink[] = [
    { to: "/kayttoehdot", label: t("footer.terms") },
    { to: "/tietosuoja", label: t("footer.privacy") },
    { to: "/evasteet", label: t("footer.cookies") },
    { to: "/palautus", label: t("footer.withdrawal") },
  ];
  return (
    <footer className="mt-32 border-t border-border bg-cream">
      <div className="container-prose py-14 md:grid md:grid-cols-12 md:gap-12 md:py-20">
        {/* Brand mark */}
        <div className="pb-10 md:col-span-4 md:pb-0 md:pr-8">
          <div className="font-display text-3xl leading-none tracking-tight text-foreground">
            Montessori<span className="italic text-clay">24</span>
          </div>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-muted-foreground">
            {t("footer.tagline")}
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.2em] text-foreground/70">
            {t("footer.hoursHeader") /* label */}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{t("footer.hoursTime")}</p>
        </div>

        {/* Link columns */}
        <div className="md:col-span-8 md:grid md:grid-cols-4 md:gap-10">
        <FooterSection title={t("footer.shop")}>
          <LinkList links={shopLinks} />
        </FooterSection>
        <FooterSection title={t("footer.support")}>
          <LinkList links={supportLinks} />
        </FooterSection>
        <FooterSection title={t("footer.legal")}>
          <LinkList links={legalLinks} />
        </FooterSection>
        <FooterSection title={t("footer.contact")}>
          <ul className="min-w-0 space-y-3 text-sm text-foreground/80">
            <li className="text-foreground">MLR Solutions OY</li>
            <li className="text-muted-foreground">{t("footer.businessId")}: 3582909-7</li>
            <li className="text-muted-foreground">Suomi</li>
            <li>
              <a href="mailto:asiakaspalvelu@montessori24.fi" className="block break-all transition-colors hover:text-clay">
                asiakaspalvelu@montessori24.fi
              </a>
            </li>
            <li className="text-muted-foreground">{t("footer.location")}</li>
            <li className="pt-1">
              <a
                href="https://www.tiktok.com/@montessori24fi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-[44px] items-center gap-2 transition-colors hover:text-clay"
                aria-label="Montessori24 TikTok"
              >
                <svg aria-hidden className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                TikTok
              </a>
            </li>
          </ul>
        </FooterSection>
        </div>
      </div>

      {/* Promise strip — three circular hallmarks */}
      <div className="border-t border-border/70 bg-background/40">
        <div className="container-prose grid grid-cols-3 gap-3 py-8 md:gap-8 md:py-10">
          <PromiseBadge icon={<Sparkles className="h-4 w-4" strokeWidth={1.5} />} label={t("footer.trust.promiseFinnish")} />
          <PromiseBadge icon={<ShieldCheck className="h-4 w-4" strokeWidth={1.5} />} label={t("footer.trust.promiseCE")} />
          <PromiseBadge icon={<Leaf className="h-4 w-4" strokeWidth={1.5} />} label={t("footer.trust.promiseReturns")} />
        </div>
      </div>

      {/* Payments + consumer protection */}
      <div className="border-t border-border/70 bg-background/30">
        <div className="container-prose grid gap-10 py-12 md:grid-cols-12 md:items-start md:gap-10 lg:gap-16">
          <div className="min-w-0 md:col-span-6">
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t("footer.trust.paymentsHeader")}
            </h3>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/70">
              {["Visa", "Mastercard", "Klarna", "MobilePay", "Apple Pay", "Google Pay", t("footer.trust.banktransfer")].map((brand, i, arr) => (
                <span key={brand} className="flex items-center gap-x-6">
                  <span>{brand}</span>
                  {i < arr.length - 1 && <span aria-hidden className="text-muted-foreground">·</span>}
                </span>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{t("footer.trust.paymentsNote")}</p>
          </div>

          <div className="min-w-0 md:col-span-6">
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t("footer.trust.consumerHeader")}
            </h3>
            {isUs ? (
              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
                Secure checkout, 30-day returns, and responsive support by email. Questions about your order? Reach us at{" "}
                <a className="text-foreground underline underline-offset-4" href="mailto:asiakaspalvelu@montessori24.fi">asiakaspalvelu@montessori24.fi</a>.
              </p>
            ) : (
              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
                Noudatamme Suomen kuluttajansuojalakia. Riitatilanteissa:{" "}
                <a className="text-foreground underline underline-offset-4" href="https://kuluttajaneuvonta.fi" target="_blank" rel="noreferrer">kuluttajaneuvonta.fi</a>,{" "}
                <a className="text-foreground underline underline-offset-4" href="https://www.kuluttajariita.fi/" target="_blank" rel="noreferrer">kuluttajariita.fi</a>.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Legal bar */}
      <div className="border-t border-border/70">
        <div className="container-prose flex flex-col items-start gap-5 py-7 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between md:gap-6 md:py-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <CountrySwitcher variant="footer" />
            <span aria-hidden className="hidden text-muted-foreground md:inline">·</span>
            <span>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
            <span>{t("footer.vat")}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/kayttoehdot" className="transition-colors hover:text-foreground">{t("footer.terms")}</Link>
            <Link to="/tietosuoja" className="transition-colors hover:text-foreground">{t("footer.privacyShort")}</Link>
            <Link to="/evasteet" className="transition-colors hover:text-foreground">{t("footer.cookiesShort")}</Link>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("m24:consent-open"));
                }
              }}
              className="transition-colors hover:text-foreground"
            >
              {t("consent.manage")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

function PromiseBadge({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2 text-center md:flex-row md:justify-center md:gap-3 md:text-left">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-clay/40 text-clay">
        {icon}
      </span>
      <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-foreground/70 md:text-xs md:tracking-[0.12em]">
        {label}
      </span>
    </div>
  );
}
